import { buildPriceLabels } from "../axes/priceAxis";
import { MAIN_PANE_ID } from "./indicatorTypes";

const toTick = (priceScale) => (price) => ({ price, y: priceScale.priceToY(price) });

/**
 * Prices labelled on the scale of an oscillator pane, as in PO: a pane with
 * levels labels only them (RSI 70 / 50 / 30), the others the highest and the
 * lowest visible value and zero between them (the middle when zero is not in view).
 */
const paneTicks = (indicator, priceScale, range) => {
  const { levelValues } = indicator;

  if (levelValues.length) {
    return levelValues.map(toTick(priceScale)).sort((upper, lower) => upper.y - lower.y);
  }

  const values = indicator.priceRange(range.first, range.last);

  if (!values) return [];

  const { min, max } = values;

  if (min === max) return [toTick(priceScale)(max)];

  const middle = min < 0 && max > 0 ? 0 : (min + max) / 2;

  return [max, middle, min].map(toTick(priceScale));
};

/**
 * Oscillator panes of a frame, top to bottom under the price pane (paneStack
 * places them): each autoscales its price scale to the visible values and
 * levels of its indicator and gets its own price labels (y in CSS px of the
 * chart). `visibleTop` / `visibleHeight`: the part of the pane inside the
 * area of the panes, the rest is scrolled away.
 *
 * @param {object} params
 * @param {object[]} params.slots - paneStack.arrange
 * @param {number} params.areaTop - top of the area of the panes (the price pane height)
 * @param {number} params.areaHeight
 * @param {number} params.width - pane width, CSS px
 * @param {{ first: number, last: number }} params.range - visible bars
 * @param {number} params.pairDecimals - for oscillators measured in prices (MACD, ATR)
 * @param {(text: string, font: string) => number} params.measure
 * @param {string} params.fontFamily
 * @returns {Array<{ id: string, top: number, width: number, height: number, visibleTop: number,
 *   visibleHeight: number, isExpanded: boolean, priceScale: object, priceLabels: object[], decimals: number }>}
 */
export const layoutIndicatorPanes = ({
  slots,
  areaTop,
  areaHeight,
  width,
  range,
  pairDecimals,
  measure,
  fontFamily,
}) =>
  slots.map(({ indicator, top, height, isExpanded }) => {
    const { paneScale: priceScale, definition } = indicator;
    const decimals = definition.decimals ?? pairDecimals;
    const visibleTop = Math.max(top, areaTop);
    const visibleBottom = Math.min(top + height, areaTop + areaHeight);

    priceScale.setHeight(height);
    priceScale.autoscale(indicator.priceRange(range.first, range.last));

    const ticks = paneTicks(indicator, priceScale, range).map((tick) => ({ ...tick, y: tick.y + top }));

    return {
      id: indicator.paneId,
      top,
      width,
      height,
      visibleTop,
      visibleHeight: Math.max(0, visibleBottom - visibleTop),
      isExpanded,
      priceScale,
      priceLabels: buildPriceLabels(ticks, decimals, measure, fontFamily),
      decimals,
    };
  });

/**
 * Pane under a point of the chart: the price pane or the visible part of an oscillator pane.
 *
 * @param {{ pane: object, indicatorPanes?: object[] }} layout - of the frame
 * @param {number} y - CSS px of the chart
 * @returns {object|null} an indicator pane from layoutIndicatorPanes, `{ id: MAIN_PANE_ID, top, height }`
 *   for the price pane, null outside the panes
 */
export const paneAtY = (layout, y) => {
  if (y >= 0 && y < layout.pane.height) return { id: MAIN_PANE_ID, top: 0, height: layout.pane.height };

  return (
    (layout.indicatorPanes || []).find(
      (pane) => y >= pane.visibleTop && y < pane.visibleTop + pane.visibleHeight,
    ) || null
  );
};
